<template>
  <div>
    <v-toolbar v-if="mobileDisplay" fixed app scroll-off-screen :scroll-threshold="50"
               class="edval-toolbar">
      <v-btn small class="menu-show-btn menu-show-btn-toolbar" @click="visible=true">
        <v-icon>chevron_right</v-icon>
        <v-icon>chevron_right</v-icon>
      </v-btn>
    </v-toolbar>
    <v-navigation-drawer app dark class="edval-drawer" v-model="visible" :width="displayWidth"
                         :mobile-break-point="600">
      <!-- We should be reading the mobile break point from $vuetify.breakpoint.thresholds but
            the version of Vuetify that we're using doesn't support it -->
      <v-list>
        <v-list-tile>
          <div class="logoContainer">
            <img src="@/assets/logo.dark.png"/>
            <div class="logoCover"></div>
          </div>
        </v-list-tile>
        <v-list-tile class="menu-title">
          <div class="menu-title-content">
            <div :class="menuHeader">MENU</div>
            <v-btn small class="menu-show-btn" @click="condensed = !condensed">
              <v-icon>{{ chevronType }}</v-icon>
              <v-icon>{{ chevronType }}</v-icon>
            </v-btn>
          </div>
        </v-list-tile>
        <template v-for="(item, i) in menuItems">
          <v-list-tile ref="menuLink" :to="item.to"
                       @click="handleClick(item, i)" :class="menuOpenClasses(item)"
                       active-class="">
            <div class="menu-item">
              <v-list-tile-action class="menu-item-icon">
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-tile-action>
              <v-list-tile-content :class="menuItemTextClasses">
                <span>{{ item.label }}</span>
              </v-list-tile-content>
            </div>
          </v-list-tile>
        </template>
      </v-list>
    </v-navigation-drawer>
    <sub-menu :visible="submenuOpen" :position="displayWidth" :full-width="mobileDisplay"
              :centerpoint="submenuCenterpoint" :items="submenuItems" @closeMenu="closeSubmenu"/>
    <version-modal v-model="showVersion"/>
    <div :class="versionClasses">
      <div class="version-inner">
        <a @click="showVersion = true">{{ version }}</a>
      </div>
    </div>
  </div>
</template>

<script>
  import menu from '@/menu';
  import SubMenu from './SubMenu';
  import VersionModal from './version/Index';

  export default {
    components: { SubMenu, VersionModal },
    data() {
      const menuItems = menu(this.$configuration, this.$store);

      // This is to clean up the localStorage key we are no longer using on the client machines
      // TODO remove this at some point
      localStorage.removeItem('openMenuItems');

      return {
        visible: !this.$vuetify.breakpoint.xsOnly, // Initial display
        condensed: false,
        version: this.$configuration.build.version,
        showVersion: false,
        menu: menuItems,
        submenu: null,
        submenuItems: [],
        submenuCenterpoint: 0
      };
    },
    computed: {
      mobileDisplay() {
        return this.$vuetify.breakpoint.xsOnly;
      },
      displayWidth() {
        if (!this.visible) {
          return 0;
        } else if (this.condensed) {
          return 70;
        } else {
          return 170;
        }
      },
      menuHeader() {
        const classes = ['menu-title-text', 'width-variable'];
        if (this.condensed) {
          classes.push('width0px');
        }

        return classes;
      },
      chevronType() {
        return this.condensed ? 'chevron_right' : 'chevron_left';
      },
      menuItems() {
        return this.menu.filter((i) => i.authorized())
          .map((i) => {
            if ('children' in i) {
              return {
                ...i,
                children: i.children.filter((c) => !('authorized' in c) || c.authorized())
              };
            } else {
              return i;
            }
          });
      },
      menuItemTextClasses() {
        const classes = ['menu-item-text', 'width-variable'];
        if (this.condensed) {
          classes.push('width0px');
        }

        return classes;
      },
      submenuOpen() {
        return !!this.submenu;
      },
      versionClasses() {
        const classes = ['version'];
        if (this.condensed) {
          classes.push('condensed');
        }
        if (!this.visible) {
          classes.push('hidden');
        }

        return classes;
      }
    },
    watch: {
      mobileDisplay(v) {
        this.visible = !v;
      }
    },
    methods: {
      menuOpenClasses(item) {
        const classes = ['menu-item-line'];
        if (this.submenu === item || (this.submenu === null &&
          (item.route && this.$route.path.startsWith(item.route) ||
            (item.to && this.$route.path === item.to)))) {
          classes.push('open');
        }

        return classes;
      },
      handleClick(item, i) {
        if (item.click) {
          item.click();
        }
        if (item.children && this.submenu !== item) {
          this.submenu = item;
          this.submenuItems = item.children;
          if (this.mobileDisplay) {
            this.submenuCenterpoint = 0;
            this.visible = false;
          } else {
            const boundingClientRect = this.$refs.menuLink[i].$el.getBoundingClientRect();
            this.submenuCenterpoint = (boundingClientRect.top + boundingClientRect.bottom) / 2;
          }
        } else {
          this.submenu = null;
        }
      },
      closeSubmenu(hasNavigated) {
        this.submenu = null;
        if (this.mobileDisplay && !hasNavigated) {
          this.visible = true;
        }
      }
    }
  };
</script>

<style lang="less" scoped>
  @import "../../../style/variables.less";

  .edval-drawer .menu-title .menu-title-content, .edval-toolbar {
    .menu-show-btn {
      &, &.menu-show-btn-toolbar {
        &.v-btn {
          min-width: unset;
          background-color: transparent;
          box-shadow: none;
          color: @menu-item-active-color;
        }
      }

      &:hover {
        background-color: @menu-item-active-bg;
      }

      .v-icon {
        font-size: 20px;

        &:last-child {
          margin-left: -13px;
        }
      }
    }
  }

  .edval-toolbar.v-toolbar {
    background-color: @edval-dark;
  }

  .edval-drawer {
    scrollbar-width: thin;

    &.navigation-drawer {
      background-color: @edval-dark !important;
      padding-top: 20px;
      transition: width @menu-ease-transition;

      .width-variable {
        overflow: hidden;
        transition: width @menu-ease-transition;
      }

      .v-list {
        margin-bottom: 50px;
      }

      .menu-item-line {
        color: @menu-text-color;

        &:hover, &.open {
          background-color: @menu-item-active-bg;
          color: @menu-item-active-color;
        }

        .menu-item {
          display: inherit;
          width: 100%;
          margin-left: 10px;
          font-family: @menu-font-family;
          font-weight: bold;

          &, .v-icon { // specificity issues
            color: inherit;
            transition: none;
            -webkit-transition: none;
          }

          .menu-item-icon {
            min-width: 40px;

            .v-icon {
              font-size: 22px;
            }
          }

          .menu-item-text {
            width: 88px;
            white-space: nowrap;

            &.width0px {
              width: 0;
            }
          }
        }
      }

      .menu-title {
        color: @menu-title-color;
        padding-top: 10px;
        padding-left: 5px;

        .menu-title-content {
          font-family: @menu-font-family;
          font-weight: bold;
          letter-spacing: 0.15rem;

          .menu-title-text {
            width: 50px;

            &.width0px {
              width: 0;
            }
          }

          .menu-show-btn {
            position: absolute;
            right: 3px;
            top: 5px;
          }
        }
      }

      .logoContainer {
        height: 100%;
        margin-left: 5px;

        img {
          width: 110px;
        }

        .logoCover {
          height: 100%;
          width: 15px;
          position: absolute;
          right: 0;
          top: 0;
          background-color: @edval-dark;
        }
      }
    }
  }

  .version {
    position: fixed;
    left: 0;
    bottom: 0;
    width: 100%;
    max-width: 170px;
    max-height: 50px;
    z-index: 10;
    transition: @menu-ease-transition;
    text-align: right;

    &.hidden {
      display: none;
    }

    &.condensed {
      max-width: 70px;
      margin-left: -15px;
    }

    .version-inner {
      padding-bottom: 20px;
      padding-left: 30px;
      padding-top: 5px;
      padding-right: 10px;
      margin-right: 20px;
      background-color: @edval-dark;

      a {
        color: lighten(@edval-dark, 65%);
        text-decoration: underline;
      }
    }
  }
</style>
